/**
 * Students or clients: what the games app calls the people a clinician works with, as they chose on Smarty Ears
 * Online (My account or the welcome questions; people_word in api/auth/user, PeopleWordService on the server).
 * "students" unless they chose "clients".
 *
 * peopleWord('Students') gives "Students" or "Clients" (also 'student', 'students', 'Student', 'STUDENTS');
 * peopleText('Add a student') gives "Add a client". For a clinician who chose clients, the words on screen are
 * switched as they appear (text, placeholders, titles, labels), so every picker, help panel, settings window and
 * game says clients without each one changing. Left as they are: a student's own screens (a student signed in
 * keeps "students"), "Student login(s)" (the login page and cards say it), what's typed into a field, and anything
 * inside an element with data-people-keep.
 */

const KEY = 'seo-people-word'
const WORD = /(^|[^\w:/\-.$#@])(student|Student|STUDENT)(s|S)?(?![\w\-=/])(?!\s+[Ll]ogin|\s+LOGIN)/g
const ATTRIBUTES = ['placeholder', 'title', 'aria-label', 'alt']
// Text inside these is never switched (code, and what's typed into a field).
const SKIP = { SCRIPT: 1, STYLE: 1, TEXTAREA: 1, NOSCRIPT: 1, CODE: 1 }

let word = read()
let observer = null

function read() {
    try {
        return localStorage.getItem(KEY) === 'clients' ? 'clients' : 'students'
    } catch (e) {
        return 'students'
    }
}

export function getPeopleWord() {
    return word
}

/** The word in the form given, written the same way: 'student', 'students', 'Student', 'Students', 'STUDENTS'. */
export function peopleWord(form = 'students') {
    const plural = /s$/i.test(form)
    const out = word === 'clients' ? (plural ? 'clients' : 'client') : plural ? 'students' : 'student'
    if (form === form.toUpperCase()) return out.toUpperCase()
    return /^[A-Z]/.test(form) ? out[0].toUpperCase() + out.slice(1) : out
}

/** A sentence with student(s) switched to client(s) for a clinician who chose clients. */
export function peopleText(text) {
    if (word !== 'clients' || typeof text !== 'string' || text.toLowerCase().indexOf('student') === -1) return text
    return text.replace(WORD, (m, before, w, s) => before + { student: 'client', Student: 'Client', STUDENT: 'CLIENT' }[w] + (s || ''))
}

function kept(el) {
    for (let node = el; node && node.nodeType === 1; node = node.parentNode) {
        if (node.isContentEditable || node.hasAttribute('data-people-keep')) return true
    }
    return false
}

function swapText(node) {
    const before = node.nodeValue
    if (!before || before.toLowerCase().indexOf('student') === -1) return
    const parent = node.parentNode
    if (!parent || SKIP[parent.nodeName] || kept(parent)) return
    const after = peopleText(before)
    if (after !== before) node.nodeValue = after
}

function swapAttributes(el) {
    if (kept(el)) return
    ATTRIBUTES.forEach((name) => {
        const before = el.getAttribute(name)
        if (before && before.toLowerCase().indexOf('student') !== -1) {
            const after = peopleText(before)
            if (after !== before) el.setAttribute(name, after)
        }
    })
}

function swapTree(root) {
    if (!root) return
    if (root.nodeType === 3) return swapText(root)
    if (root.nodeType !== 1 || SKIP[root.nodeName]) return
    swapAttributes(root)
    const walk = document.createTreeWalker(root, NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT)
    let node
    while ((node = walk.nextNode())) {
        if (node.nodeType === 3) swapText(node)
        else swapAttributes(node)
    }
}

function start() {
    if (observer || typeof MutationObserver === 'undefined' || !document.body) return
    swapTree(document.body)
    observer = new MutationObserver((changes) => {
        changes.forEach((c) => {
            if (c.type === 'characterData') swapText(c.target)
            else if (c.type === 'attributes') swapAttributes(c.target)
            else c.addedNodes.forEach(swapTree)
        })
    })
    observer.observe(document.body, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ATTRIBUTES })
}

function stop() {
    if (observer) observer.disconnect()
    observer = null
}

/**
 * From the signed-in account (api/auth/user): a clinician's choice, remembered on this device so the next visit
 * says it from the first screen. A student signed in always has "students".
 */
export function setPeopleWordFromUser(user) {
    const next = user && user.role === 'therapist' && user.people_word === 'clients' ? 'clients' : 'students'
    const changed = next !== word
    word = next
    try {
        localStorage.setItem(KEY, word)
    } catch (e) {}
    if (word === 'clients') {
        start()
        if (changed) swapTree(document.body)
    } else {
        stop()
        // Back to students after a change on another tab: the next screen says it (React writes its own words again).
    }
}

/** At start, from what this device remembers, before the account is loaded. */
export function startPeopleWord() {
    if (word === 'clients') start()
}
